import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, PieChart, BarChart3, TrendingUp, Calendar,
  RefreshCw, Download, Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { getTransactions, getBudgets, getUserStats } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { 
  CategoryPieChart, 
  MonthlyBarChart, 
  SpendingTrendChart,
  BudgetProgressChart 
} from '../components/SpendingCharts';
import { toast } from 'sonner';

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month'); // month, quarter, year
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [monthlyData, setMonthlyData] = useState({});
  const [categoryData, setCategoryData] = useState({});

  useEffect(() => {
    loadData();
  }, [period]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Calculate date range based on period
      const now = new Date();
      let startDate;
      let endDate = null;
      
      switch (period) {
        case 'last_month':
          startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          endDate = new Date(now.getFullYear(), now.getMonth(), 0); // Last day of previous month
          break;
        case 'quarter':
          startDate = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
          break;
        case 'last_quarter':
          const currentQuarterStart = Math.floor(now.getMonth() / 3) * 3;
          startDate = new Date(now.getFullYear(), currentQuarterStart - 3, 1);
          endDate = new Date(now.getFullYear(), currentQuarterStart, 0); // Last day of previous quarter
          break;
        case 'year':
          startDate = new Date(now.getFullYear(), 0, 1);
          break;
        default: // month
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      }
      
      const queryParams = { limit: 1000, start_date: startDate.toISOString() };
      if (endDate) {
        queryParams.end_date = endDate.toISOString();
      }
      
      const [statsRes, txnRes, budgetsRes] = await Promise.all([
        getUserStats(),
        getTransactions(queryParams),
        getBudgets()
      ]);
      
      setStats(statsRes.data);
      setTransactions(txnRes.data.transactions || []);
      setBudgets(budgetsRes.data || []);
      
      // Process data for charts
      processChartData(txnRes.data.transactions || []);
      
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error(t('loading_error'));
    } finally {
      setLoading(false);
    }
  };

  const processChartData = (txns) => {
    // Group by category
    const byCategory = {};
    txns.forEach(txn => {
      const cat = txn.category || 'divers';
      if (!byCategory[cat]) {
        byCategory[cat] = { total: 0, count: 0 };
      }
      byCategory[cat].total += txn.amount;
      byCategory[cat].count += 1;
    });
    
    // Calculate percentages
    const totalSpending = Object.values(byCategory).reduce((sum, c) => sum + c.total, 0);
    Object.keys(byCategory).forEach(cat => {
      byCategory[cat].percentage = Math.round((byCategory[cat].total / totalSpending) * 100) || 0;
    });
    
    setCategoryData(byCategory);
    
    // Group by month
    const byMonth = {};
    for (let i = 1; i <= 12; i++) {
      byMonth[i] = { total: 0, count: 0 };
    }
    
    txns.forEach(txn => {
      const date = new Date(txn.date);
      const month = date.getMonth() + 1;
      byMonth[month].total += txn.amount;
      byMonth[month].count += 1;
    });
    
    setMonthlyData(byMonth);
  };

  const totalSpending = Object.values(categoryData).reduce((sum, c) => sum + c.total, 0);
  const avgPerDay = period === 'month' 
    ? totalSpending / new Date().getDate()
    : totalSpending / (period === 'quarter' ? 90 : 365);
  const topCategory = Object.entries(categoryData).sort((a, b) => b[1].total - a[1].total)[0];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-bold text-lg">
                  {language === 'fr' ? 'Analyse Financière' : language === 'pt' ? 'Análise Financeira' : 'Financial Analytics'}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {t('visualize_spending')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">{t('this_month')}</SelectItem>
                  <SelectItem value="last_month">{t('last_month')}</SelectItem>
                  <SelectItem value="quarter">{t('quarter')}</SelectItem>
                  <SelectItem value="last_quarter">{t('last_quarter')}</SelectItem>
                  <SelectItem value="year">{language === 'fr' ? 'Année' : language === 'pt' ? 'Ano' : 'Year'}</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="ghost" size="icon" onClick={loadData}>
                <RefreshCw className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Summary Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          <Card className="bg-gradient-to-br from-primary/10 to-orange-100 dark:from-primary/20 dark:to-orange-900/20">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">
                {t('total_spent')}
              </p>
              <p className="text-2xl font-bold text-primary">{formatCurrency(totalSpending)}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">
                {t('transactions')}
              </p>
              <p className="text-2xl font-bold">{transactions.length}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">
                {t('avg_per_day')}
              </p>
              <p className="text-2xl font-bold">{formatCurrency(avgPerDay)}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">
                {t('top_category')}
              </p>
              <p className="text-lg font-bold truncate">
                {topCategory ? (t(`cat_${topCategory[0]}`) || topCategory[0]) : '-'}
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Pie Chart - Spending by Category */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <CategoryPieChart 
              data={categoryData}
              title={t('breakdown_by_category')}
            />
          </motion.div>

          {/* Bar Chart - Monthly Evolution */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <MonthlyBarChart 
              data={monthlyData}
              title={t('monthly_evolution')}
            />
          </motion.div>

          {/* Area Chart - Spending Trend */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <SpendingTrendChart 
              data={monthlyData}
              title={t('spending_trend')}
            />
          </motion.div>

          {/* Budget Progress */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <BudgetProgressChart 
              budgets={budgets}
              title={t('budget_progress')}
            />
          </motion.div>
        </div>

        {/* Category Breakdown List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {t('category_details')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(categoryData)
                  .sort((a, b) => b[1].total - a[1].total)
                  .map(([category, data]) => {
                    const categoryColors = {
                      logement: '#FF4F00', transport: '#3B82F6', alimentation: '#10B981',
                      abonnements: '#8B5CF6', shopping: '#EC4899', loisirs: '#F59E0B',
                      sante: '#EF4444', epargne: '#00D68F', education: '#6366F1',
                      animaux: '#A855F7', enfants: '#F472B6', cadeaux: '#FB923C',
                      entretien: '#78716C', professionnel: '#0EA5E9', divers: '#A8A29E'
                    };
                    
                    return (
                      <div key={category} className="flex items-center justify-between p-3 bg-muted/50 rounded-xl">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: categoryColors[category] || '#A8A29E' }}
                          />
                          <div>
                            <p className="font-medium">{t(`cat_${category}`) || category}</p>
                            <p className="text-xs text-muted-foreground">
                              {data.count} {t('transactions_label')}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold">{formatCurrency(data.total)}</p>
                          <p className="text-xs text-muted-foreground">{data.percentage}%</p>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Tips */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Card className="bg-blue-50 dark:bg-blue-950/20 border-blue-200">
            <CardContent className="p-4">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                {language === 'fr' 
                  ? '💡 Conseil : Comparez vos dépenses d\'un mois à l\'autre pour identifier les tendances et optimiser votre budget.'
                  : language === 'pt'
                  ? '💡 Dica: Compare as suas despesas de mês para mês para identificar tendências e otimizar o seu orçamento.'
                  : '💡 Tip: Compare your spending month over month to identify trends and optimize your budget.'}
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
}
